import React from 'react'
import { useHistory } from 'react-router-dom'
import { Box, Button, Grid, InputAdornment, Paper, TableRow, Typography } from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import LoadingComponent from '../../../components/ui-widgets/LoadingComponent'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import MUIDataTable, { type MUIDataTableOptions, type SelectableRows } from 'mui-datatables'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import {
  useDistributeConfirmedInstructionsMutation,
  useDistributionInstructionApiCreateBillingRunMutation,
  useGetBillingMonthRunsQuery,
  useGetPaymentConfirmedSummaryQuery,
  useSetCurrencyConversionExchangeRateMutation
} from '../../../../api-services/asset-holdings/distributionInstructionApi'
import {
  type BillingRunMonthlyDetail,
  type CurrencyConversionDetail,
  type CurrencyConversionSummary,
  type StatusCounterDto
} from '../../../../api-schema-types/asset-holdings/DistributionInstructionDomainDtos'
import RatedUsageCounterChip from '../../../../views/components/chips/RatedUsageCounterChip'
import AssetDistributionCounterChip from '../../../../views/components/chips/AssetDistributionCounterChip'
import CurrencyCodeChip from '../../../components/chips/CurrencyCodeChip'
import { NumberFormatter } from '../../../../utility/formatters/NumberFormatter'
import AssetDistributionStatusChip from '../../../../views/components/chips/AssetDistributionStatusChip'
import Refresh from '@mui/icons-material/Refresh'
import TextField from '@mui/material/TextField'
import PriceChangeIcon from '@mui/icons-material/PriceChange'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  selectBillingMonth,
  selectExchangeRate,
  setBillingMonth,
  setDistributeInstructionsEnabled,
  setExchangeRate
} from './editDistributionInstructionsSlice'
import { formatDateAsApiFormat, formatDateStringAsApiFormat } from '../../../../utility/formatters/DateFormatter'

const WeeklyDistributionsPage: React.FC = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const sixMonthsBefore: Date = startOfMonth(subMonths(new Date(), 6))
  const previousMonth: Date = subMonths(new Date(), 1)
  // const distributeInstructionsEnabled: boolean = useAppSelector<boolean>(selectDistributeInstructionsEnabled)
  const exchangeRate: string = useAppSelector<string>(selectExchangeRate)
  const billingMonth: string | null = useAppSelector<string | null>(selectBillingMonth)

  if (billingMonth === null) {
    dispatch(setBillingMonth(formatDateAsApiFormat(startOfMonth(previousMonth))))
  }
  //

  // const [exchangeRate, setExchangeRate] = useState<string>('')
  const goTo = (location: string): void => {
    history.push(location)
  }

  const getMonthlyRunsApiResult = useGetBillingMonthRunsQuery({})
  const getPaymentConfirmedSummaryQuery = useGetPaymentConfirmedSummaryQuery({})
  const refetchMonthlyRunsApiResult = getMonthlyRunsApiResult.refetch
  const refetchPaymentConfirmedSummaryQuery = getPaymentConfirmedSummaryQuery.refetch
  const [apiCreateMonthlyBillingRunTrigger] = useDistributionInstructionApiCreateBillingRunMutation()
  const [apiSetEchangeRateTrigger] = useSetCurrencyConversionExchangeRateMutation()
  const [apiDistributeConfirmedInstructionsTrigger] = useDistributeConfirmedInstructionsMutation()

  const handleExchangeRateChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const exchangeRateValue: string = event.target.value
    if (exchangeRateValue.length >= 3 || exchangeRateValue.length === 0) {
      dispatch(setExchangeRate(exchangeRateValue))
    }
  }
  const handleSetExchangeRate = (currencyConversionSummary: CurrencyConversionSummary): void => {
    const setExchangeRatePayload = {
      srcCurrencyId: currencyConversionSummary.srcCurrencyId,
      currencyId: currencyConversionSummary.currencyId,
      exchangeRate
    }
    const choice = window.confirm(
      'Are you sure you want to ?'
    )
    if (choice) {
      apiSetEchangeRateTrigger(setExchangeRatePayload)
        .unwrap()
        .then((triggerResult) => {
          dispatch(setDistributeInstructionsEnabled(true))
          dispatch(setNotification({ open: true, severity: 'success', message: 'The Exchange Rate has been set and the Instruction\'s have been marked as Totals Confirmed.\n' }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
      void handleRefetch()
    }
  }
  const handleDistributeConfirmedInstructions = async (): Promise<void> => {
    const choice = window.confirm(
      'Are you sure you want to ?'
    )
    if (choice) {
      apiDistributeConfirmedInstructionsTrigger({})
        .unwrap()
        .then((triggerResult) => {
          dispatch(setDistributeInstructionsEnabled(false))
          dispatch(setNotification({
            open: true,
            severity: 'success',
            message: 'Instruction\'s have been queued and are being processed.\n'
          }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
      await handleRefetch()
    }
  }
  const handleCreateBillingRun = async (): Promise<void> => {
    if (billingMonth != null) {
      const billingMonthDate = new Date(billingMonth)
      let billingMonthIndex = billingMonthDate.getMonth()
      const createBillingRunPayload = {
        billingRunMonth: ++billingMonthIndex,
        billingRunYear: billingMonthDate.getFullYear()
      }
      apiCreateMonthlyBillingRunTrigger(createBillingRunPayload)
        .unwrap()
        .then((triggerResult) => {
          dispatch(setDistributeInstructionsEnabled(true))
          dispatch(setNotification({ open: true, severity: 'success', message: `Monthly Billing Run has been created for ${createBillingRunPayload.billingRunMonth} - ${createBillingRunPayload.billingRunYear}` }))
        })
        .catch((err: any) => {
          dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
        })
      await handleRefetch()
    }
  }
  const isSetExchangeRateDisabled = (conversionRowIndex: number): boolean => {
    if (getPaymentConfirmedSummaryQuery.isSuccess) {
      return !(getPaymentConfirmedSummaryQuery.data.conversions[conversionRowIndex].amountTotal === 0 && getPaymentConfirmedSummaryQuery.data.conversions[conversionRowIndex].srcCurrencyId !== getPaymentConfirmedSummaryQuery.data.conversions[conversionRowIndex].currencyId)
    }
    return false
  }

  const isCreateBillingRunEnabled = (): boolean => {
    if (getMonthlyRunsApiResult.isSuccess && billingMonth !== null) {
      const billingMonthDate = new Date(billingMonth)
      const billingRuns: BillingRunMonthlyDetail[] = getMonthlyRunsApiResult.data
      let billingMonthIndex = billingMonthDate.getMonth()
      billingMonthIndex = ++billingMonthIndex
      for (const billingRun of billingRuns) {
        if (billingRun.billingYear === billingMonthDate.getFullYear() && billingRun.billingMonth === billingMonthIndex) {
          return true
        }
      }
    }
    return false
  }

  const isDistributeInstructionsDisabled = (): boolean => {
    if (getPaymentConfirmedSummaryQuery.isSuccess && getPaymentConfirmedSummaryQuery.data.paymentConfirmedDistributions !== null && getPaymentConfirmedSummaryQuery.data.paymentConfirmedDistributions.length > 0) {
      return !(getPaymentConfirmedSummaryQuery.data.paymentConfirmedDistributions[0].assetDistributionInstruction.status === 'TOTALS_CONFIRMED')
    }
    return false
  }

  const renderAssetRatedStatus = (statuses: StatusCounterDto[]): React.ReactElement[] => {
    const statusCounters: React.ReactElement[] = []
    for (const detail of statuses) {
      statusCounters.push(<RatedUsageCounterChip key={detail.status} assetRatedStatus={detail.status} ratedCount={detail.countPerStatus}/>)
    }
    return statusCounters
  }

  const renderAssetDistributionStatus = (statuses: StatusCounterDto[]): React.ReactElement[] => {
    const statusCounters: React.ReactElement[] = []
    for (const detail of statuses) {
      statusCounters.push(<AssetDistributionCounterChip key={detail.status} itemStatus={detail.status} statusCount={detail.countPerStatus}/>)
    }
    return statusCounters
  }

  const handleRefetch = async (): Promise<void> => {
    await refetchMonthlyRunsApiResult()
    await refetchPaymentConfirmedSummaryQuery()
  }

  const conversionsSummaryColumns = [
    {
      name: 'srcCurrencyCode',
      label: 'Source Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={tableMeta.rowData[1].toString()}/>
          )
        }
      }
    },
    {
      name: 'srcCurrency',
      label: 'Source Currency',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'srcAmountTotal',
      label: 'Source Total',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'currencyCode',
      label: 'Currency Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={tableMeta.rowData[4].toString()}/>
          )
        }
      }
    },
    {
      name: 'currency',
      label: 'Currency',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'amountTotal',
      label: 'Amount Total',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'countDistributionInstructions',
      label: '# Instructions',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'exchangeRate',
      label: 'Exchange Rate',
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
              <div>
                { getPaymentConfirmedSummaryQuery.isSuccess && !isSetExchangeRateDisabled(tableMeta.rowIndex) && (
                  <TextField
                    label="Exchange Rate"
                    sx={{ width: '100%', mb: 2 }}
                    placeholder="Exchange Rate"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <PriceChangeIcon />
                        </InputAdornment>
                      )
                    }}
                    onChange={handleExchangeRateChange}
                  />
                )}
                { getPaymentConfirmedSummaryQuery.isSuccess && isSetExchangeRateDisabled(tableMeta.rowIndex) && (
                  <span>{value}</span>
                )}
              </div>
          )
        }
      }
    },
    {
      name: '',
      label: '',
      options: {
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
              <div>
                { getPaymentConfirmedSummaryQuery.isSuccess && (
                <Button variant="text" onClick={() => {
                  handleSetExchangeRate(getPaymentConfirmedSummaryQuery.data.conversions[tableMeta.rowIndex])
                }} disabled={isSetExchangeRateDisabled(tableMeta.rowIndex)} >
                  Set Exchange Rate
                </Button>
                )}
              </div>
          )
        }
      }
    }
  ]

  const monthlyBillingColumns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'billingYear',
      label: 'Year',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'billingMonth',
      label: 'Month',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'billingPeriodStart',
      label: 'Period Start',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'billingPeriodEnd',
      label: 'Period End',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'usageStatuses',
      label: 'Asset Rated Usages',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          if (getMonthlyRunsApiResult.isSuccess) {
            return (
              renderAssetRatedStatus(getMonthlyRunsApiResult.data[tableMeta.rowIndex].assetRatedStatuses)
            )
          } else {
            return (<span />)
          }
        }
      }
    },
    {
      name: 'assetDistributionsStatuses',
      label: 'Distribution Instructions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          if (getMonthlyRunsApiResult.isSuccess) {
            return (
              renderAssetDistributionStatus(getMonthlyRunsApiResult.data[tableMeta.rowIndex].assetDistributionsStatuses)
            )
          } else {
            return (<span />)
          }
        }
      }
    },
    {
      name: '',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
          <Button onClick={() => { goTo(`/app/distributions/run/${tableMeta.rowData[0]}`) }} type={'button'} variant={'text'}>
            View
          </Button>
          )
        }
      }
    }
  ]

  const pendingDistributionsColumns = [
    {
      name: 'assetRatedUsage.billingRunMonthlyId',
      label: 'Billing Run Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'assetDistributionInstruction.id',
      label: 'Id',
      options: {
        filter: false,
        sort: true
      }
    },
    {
      name: 'assetDistributionInstruction.projectId',
      label: 'Project Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'assetDistributionInstruction.projectName',
      label: 'Project',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'assetDistributionInstruction.currencyId',
      label: 'Currency Id',
      options: {
        filter: true,
        sort: true
      }
    },
    {
      name: 'assetDistributionInstruction.currencyCode',
      label: 'Currency Code',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <CurrencyCodeChip code={value} name={value}/>
          )
        }
      }
    },
    {
      name: 'assetDistributionInstruction.numberCellOwnerAgreements',
      label: '# COAs',
      options: {
        filter: false,
        sort: true
      }
    },
    {
      name: 'assetDistributionInstruction.numberOfCells',
      label: '# Cells',
      options: {
        filter: false,
        sort: true
      }
    },
    {
      name: 'assetDistributionInstruction.amountToDistribute',
      label: 'Amount To Distribute',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'assetDistributionInstruction.amountPerCell',
      label: 'Amount Per Cell',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<span>{NumberFormatter.roundToString(value, 8)}</span>)
        }
      }
    },
    {
      name: 'assetDistributionInstruction.status',
      label: 'Status',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (<AssetDistributionStatusChip itemStatus={value}/>)
        }
      }
    }
  ]

  const optionsCurrencyConversionTotals: MUIDataTableOptions = {
    expandableRows: false,
    expandableRowsOnClick: false,
    selectableRowsHideCheckboxes: true,
    selectableRows: 'none' as SelectableRows,
    fixedHeader: true
  }

  const optionsMonthlyBillings: MUIDataTableOptions = {
    expandableRows: false,
    expandableRowsOnClick: false,
    selectableRows: 'none' as SelectableRows,
    fixedHeader: true
  }

  const optionsDistributionInstruction: MUIDataTableOptions = {
    expandableRows: true,
    enableNestedDataAccess: '.',
    selectableRowsHideCheckboxes: true,
    selectableRows: 'none' as SelectableRows,
    expandableRowsOnClick: true,
    renderExpandableRow: (rowData: string[], rowMeta: any) => {
      let instructionConversions: CurrencyConversionDetail[] = []
      if (getPaymentConfirmedSummaryQuery.isSuccess) {
        instructionConversions = getPaymentConfirmedSummaryQuery.data.paymentConfirmedDistributions[rowMeta.dataIndex].conversions
        return (
          <React.Fragment>
            <tr>
              <td colSpan={10}>
                <TableContainer component={Paper}>
                  <Table style={{ minWidth: '650' }} aria-label="simple table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Source Currency Id</TableCell>
                        <TableCell align="right">Source Currency</TableCell>
                        <TableCell align="right">Source Amount</TableCell>
                        <TableCell align="right"># Of Cells</TableCell>
                        <TableCell align="right">Currency Id</TableCell>
                        <TableCell align="right">Currency</TableCell>
                        <TableCell align="right">amount</TableCell>
                        <TableCell align="right">exchangeRate</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {instructionConversions.map((conversion: CurrencyConversionDetail) => (
                        <TableRow key={conversion.id}>
                          <TableCell>{conversion.srcCurrencyId}</TableCell>
                          <TableCell align="right"><CurrencyCodeChip code={conversion.srcCurrencyCode} name={conversion.srcCurrency}/></TableCell>
                          <TableCell align="right">{NumberFormatter.roundToString(conversion.srcAmount, 8)}</TableCell>
                          <TableCell align="right">{conversion.numberOfCells}</TableCell>
                          <TableCell align="right">{conversion.currencyId}</TableCell>
                          <TableCell align="right"><CurrencyCodeChip code={conversion.currencyCode} name={conversion.currency}/></TableCell>
                          <TableCell align="right">{NumberFormatter.roundToString(conversion.amount, 8)}</TableCell>
                          <TableCell align="right">{conversion.exchangeRate}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </td>
            </tr>
          </React.Fragment>
        )
      }
    }
  }

  return (
    <>
    <Box sx={{ display: 'flex' }}>
      <Box sx={{ p: 1, flexGrow: 1 }}>
        <Typography variant={'h4'}>Weekly Distributions</Typography>
      </Box>
      <Box sx={{ p: 1 }}>
        <Button variant={'outlined'} type="button" onClick={handleRefetch}>
          <Refresh/>
        </Button>
      </Box>
      <Box sx={{ p: 1 }}>
        <Button variant={'outlined'} type="button" onClick={() => { goTo('/app/finance-area') }}>
          <HomeIcon />
        </Button>
      </Box>
    </Box>
    {getPaymentConfirmedSummaryQuery.isLoading && <LoadingComponent />}
    {(getPaymentConfirmedSummaryQuery.error != null) && <ErrorComponent message={RtkQueryHelper.mapResponseError(getPaymentConfirmedSummaryQuery.error)} />}
    { getPaymentConfirmedSummaryQuery.isSuccess &&
      getPaymentConfirmedSummaryQuery.data.paymentConfirmedDistributions.length > 0 && (
      <Paper>
        <Box sx={{ p: 4, mt: 3 }}>
          <Grid container justifyContent="right" sx={{ mb: 2 }}>
            <Grid item>
              <Grid container spacing={2}>
                <Grid item>
                  <Button variant={'outlined'} type="button" onClick={handleDistributeConfirmedInstructions} disabled={isDistributeInstructionsDisabled()}>
                    <CloudUploadIcon/>
                    Distribute Confirmed Instructions
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
             <MUIDataTable
               title = {'Currency Conversion Totals - Distribution Instructions'}
               data = { getPaymentConfirmedSummaryQuery.data.conversions }
               columns = { conversionsSummaryColumns }
               options = { optionsCurrencyConversionTotals }>
             </MUIDataTable>

           <Grid container spacing={1}>
             <Grid item md={12} sx={{ mt: 1 }}></Grid>
           <Grid item md={2}>
             <Typography variant={'body2'}>Count Conversions</Typography>
             <Typography variant={'subtitle1'}>
               <strong>{getPaymentConfirmedSummaryQuery.data.conversions.length}</strong>
             </Typography>
           </Grid>
           <Grid item md={2}>
             <Typography variant={'body2'}>Total Conversions Amount</Typography>
             <Typography variant={'subtitle1'}>
               <strong>{getPaymentConfirmedSummaryQuery.data.conversionSummaries.totalConversionAmount}</strong>
             </Typography>
           </Grid>
           <Grid item md={2}>
             <Typography variant={'body2'}>Total Instructions Amount</Typography>
             <Typography variant={'subtitle1'}>
               <strong>{getPaymentConfirmedSummaryQuery.data.conversionSummaries.totalInstructionAmount}</strong>
             </Typography>
           </Grid>
           <Grid item md={2}>
             <Typography variant={'body2'}>Count Instructions</Typography>
             <Typography variant={'subtitle1'}>
               <strong>{getPaymentConfirmedSummaryQuery.data.paymentConfirmedDistributions.length}</strong>
             </Typography>
           </Grid>
             <Grid item md={12} sx={{ mt: 1 }}></Grid>
           </Grid>

            <MUIDataTable
              title = {'Distribution Instructions - being processed'}
              data = { getPaymentConfirmedSummaryQuery.data.paymentConfirmedDistributions }
              columns = { pendingDistributionsColumns }
              options = { optionsDistributionInstruction }>
            </MUIDataTable>
        </Box>
      </Paper>
    )}
      <Paper>
        <Box sx={{ p: 4, mt: 3 }}>
          { getMonthlyRunsApiResult.isSuccess && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex' }}>
              <Box sx={{ p: 1, flexGrow: 1 }}>
              </Box>
              <Box sx={{ p: 1 }}>
                <DatePicker
                    views={['year', 'month']}
                    label='Choose Billing Month'
                    minDate={sixMonthsBefore}
                    maxDate={endOfMonth(previousMonth)}
                    value={formatDateStringAsApiFormat(billingMonth)}
                    onChange={(newValue: Date | null) => {
                      dispatch(setBillingMonth(formatDateAsApiFormat(startOfMonth(previousMonth))))
                    }}
                    renderInput={(params: any) => (
                        <TextField helperText='Can only choose from 6 month window' {...params}
                                   sx={{ width: '100%' }} />
                    )}
                />
              </Box>
              <Box sx={{ p: 1 }}>
                {(billingMonth != null) && (
                    <Button variant={'contained'} type='button' onClick={handleCreateBillingRun}
                            disabled={isCreateBillingRunEnabled()}>
                      Create Billing
                      for {format(new Date(billingMonth), 'MMM')} {format(new Date(billingMonth), 'yyyy')}
                    </Button>
                )}
              </Box>
            </Box>
          </LocalizationProvider>
          )}
          { getMonthlyRunsApiResult.isLoading && <LoadingComponent />}
          { getMonthlyRunsApiResult.isError && <ErrorComponent message={JSON.stringify(getMonthlyRunsApiResult.error)} />}
          { getMonthlyRunsApiResult.isSuccess && (
            <MUIDataTable
              title = {'Monthly Billing Runs - Summary'}
              data = { getMonthlyRunsApiResult.data }
              columns = { monthlyBillingColumns }
              options = { optionsMonthlyBillings }>
            </MUIDataTable>
          )}
        </Box>
      </Paper>
    </>
  )
}

export default WeeklyDistributionsPage
